import type { MaybeRef } from '@vueuse/core'
import type dayjs from 'dayjs'
import type { PromisifyFn } from '~/api/interface/base'

/**
 * 获取随机长度组合字母数字
 * @param len 长度
 * @returns string
 */
export function randomString(len: number) {
  const curlen = len || 32
  const chars = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678'
  const maxPos = chars.length
  let pwd = ''
  for (let i = 0; i < curlen; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * maxPos))
  }

  return pwd
}

/**
 * TS Omit 方法
 *
 * 过滤掉所选的key值
 * @param 对象
 * @param 待过滤的数据
 */
export function omit<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> {
  // return Object.entries(obj).reduce((prev, [k, v]) => {
  //   !keys.includes(k as K) && (prev[k as K] = v)
  //   return prev
  // }, {} as T)

  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => !keys.includes(k as K)),
  ) as Omit<T, K>
}

/** TS Pick 方法 */
export function pick<T extends Record<string, any>, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> {
  // return keys.reduce((acc, key) => {
  //   if (Object.prototype.hasOwnProperty.call(obj, key)) {
  //     acc[key] = obj[key]
  //   }
  //   return acc
  // }, {} as Pick<T, K>)

  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => keys.includes(k as K)),
  ) as Pick<T, K>
}

/**
 * 串式命名 -> 小驼峰
 * aaa-bbb-ccc -> aaaBbbCcc
 */
export function kebabToLowerCamel(str: string) {
  return str.replace(/-([a-z]{1})/g, (_, v) => v.toLocaleUpperCase())
}

/** 客户端打印日志 */
export const clientLog = process.client
  ? console
  : new Proxy(console, { get: () => () => { } })

/** 服务端打印日志 */
export const serverLog = process.server
  ? console
  : new Proxy(console, { get: () => () => { } })

/** 处理多语言参数，br转pt，pt转br */
export function handleLocale(locale: MaybeRef<string>) {
  const localeKey = unref(locale)
  if (localeKey === 'pt') {
    return 'br'
  }
  else if (localeKey === 'br') {
    return 'pt'
  }

  return localeKey
}

/** 根据字典表排列对象 小写 */
export function sortRecordByProperty(record: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(record).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
  )
}

/**
 * 在body中创建a标签
 * @param url a标签链接
 * @param options a标签配置
 */
export function createATagInBody(url: string, options?: Record<string, string>) {
  const tempLink = document.createElement('a')
  tempLink.style.display = 'none'
  tempLink.href = url ?? ''

  if (options) {
    for (const name in options) {
      tempLink.setAttribute(name, options[name])
    }
  }

  document.body.appendChild(tempLink)
  tempLink.click()
  document.body.removeChild(tempLink)
}

/**
 * 给请求地址添加i18n地区前缀
 */
export function appendLocaleApi(api: string) {
  const { userLocale } = storeToRefs(userStore())
  const url = api.replace('{countryCode}', userLocale.value)

  return url
}

/** 语言、货币发生变化时, 主动调用方法才去触发一次 */
export function useChangeLangCurrencyCallOnce(fn: PromisifyFn | Function) {
  const cacheParams = useState('cacheParams', () => ({ currency: '', locale: '' }))

  const { userLocale } = storeToRefs(userStore())
  const { currentCurrency } = storeToRefs(useCurrencyStore())

  // 当前参数
  const currentParams = { currency: currentCurrency.value, locale: userLocale.value }

  /** 检查参数是否发生变化 */
  const hasChanged = cacheParams.value.currency !== currentParams.currency || cacheParams.value.locale !== currentParams.locale

  if (hasChanged) {
    handleUpdate()
  }

  /** 更新缓存并执行逻辑 */
  async function handleUpdate(...args: any) {
    cacheParams.value = { ...currentParams } // 更新缓存

    await fn(...args)
  }

  /** 销毁缓存数据 */
  function destroyCache() {
    cacheParams.value = { currency: '', locale: '' }
  }

  return {
    destroyCache,
  }
}

/** 开发环境打印 */
export function devLog(value: any) {
  if (isTest()) {
    console.warn(value)
  }
}

/** 判断是否是图片链接 */
export function isImageType(filename: string): boolean {
  if (!filename || typeof filename !== 'string') {
    return false
  }
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'ico']

  const extension = filename?.split('.')?.pop()?.toLowerCase()

  return imageExtensions.includes(extension || '')
}

const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

export function formatToDateTime(
  date: dayjs.Dayjs | number | undefined = undefined,
  format = DATE_TIME_FORMAT,
): string {
  const dayjs = useDayjs()

  return dayjs(date).tz('Asia/Shanghai').format(format)
}

/** sha256 散列处理 */
export async function sha256(value: string) {
  const encoder = new TextEncoder()
  const data = encoder.encode(value)
  const hash = await crypto.subtle.digest('SHA-256', data)
  return encode(hash)
}

function encode(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/**
 *  根据指定的键合并两个对象数组。
 * 此函数遍历第一个数组 (arr1)，并尝试在第二个数组 (arr2) 中找到
 * 具有相同指定键值的对应对象。如果找到，它会将第二个数组中的对象的
 * 属性合并到第一个数组中的对象上（arr2 的属性会覆盖 arr1 的相同属性）。
 * 如果未找到，则保留 arr1 中的原始对象。
 */
export function mergeByKey<
  T extends Record<string, any>,
  U extends Record<string, any>,
  K extends keyof T & keyof U,
>(
  arr1: T[],
  arr2: U[],
  key: K,
): T[] {
  const map = new Map<T[K], U>(arr2.map(i => [i[key] as unknown as T[K], i]))
  return arr1.map((item) => {
    const found = map.get(item[key])
    return found ? { ...item, ...found } : item
  })
}
